import { Alert, Button, Divider, Drawer, Grid, IconButton, Snackbar, TextField, Typography } from '@mui/material'
import { lineItemType, shipToType } from '../types'
import { ArrowForward } from '@mui/icons-material'
import { useEffect, useState } from 'react'
import './ShippingData.css'
import axios from 'axios'
import { fieldLengths } from '../fieldLengths'

export default function ShippingData({
  lineItem,
  defaultShipTo,
  open,
  onClose,
  onSaved,
}: {
  lineItem: lineItemType
  defaultShipTo: shipToType
  open: boolean
  onClose: () => void
  onSaved?: (updatedShipTo: shipToType) => void
}) {
  const getShipToFromLineItem = (lineItem: lineItemType, defaultShipTo: shipToType) => {
    return [
      lineItem.ShipToAttention,
      lineItem.ShipToCompany,
      lineItem.ShipToAddress1,
      lineItem.ShipToAddress2,
      lineItem.ShipToAddressCity,
      lineItem.ShipToAddressState,
      lineItem.ShipToAddressZip,
      lineItem.ShipToAddressCountry,
      lineItem.ReceiverContactName,
      lineItem.ReceiverContactEmail,
      lineItem.ReceiverContactPhone,
    ].every(value => !value)
      ? structuredClone(defaultShipTo)
      : structuredClone({
          ShipToAttention: lineItem.ShipToAttention,
          ShipToCompany: lineItem.ShipToCompany,
          ShipToAddress1: lineItem.ShipToAddress1,
          ShipToAddress2: lineItem.ShipToAddress2,
          ShipToAddressCity: lineItem.ShipToAddressCity,
          ShipToAddressState: lineItem.ShipToAddressState,
          ShipToAddressZip: lineItem.ShipToAddressZip,
          ShipToAddressCountry: lineItem.ShipToAddressCountry,
          ReceiverContactName: lineItem.ReceiverContactName,
          ReceiverContactEmail: lineItem.ReceiverContactEmail,
          ReceiverContactPhone: lineItem.ReceiverContactPhone,
        })
  }
  const [shipToEdit, setShipToEdit] = useState(() => getShipToFromLineItem(lineItem, defaultShipTo))
  const [saveStatus, setSaveStatus] = useState<'' | 'loading' | 'success' | 'error'>('')

  useEffect(() => {
    if (open) {
      setShipToEdit(getShipToFromLineItem(lineItem, defaultShipTo))
    } else {
      setShipToEdit({} as shipToType)
    }
  }, [open])

  const handleSave = () => {
    setSaveStatus('loading')
    axios
      .patch(`/order/${lineItem.Id}/editShipping/${lineItem.Number}`, {
        shippingData: shipToEdit,
      })
      .then(() => {
        onSaved?.(shipToEdit)
        onClose()
        setSaveStatus('success')
      })
      .catch(err => {
        console.error(err)
        setSaveStatus('error')
      })
  }

  return (
    <>
      <Drawer open={open} onClose={onClose} anchor="right">
        <div className="shipping-data">
          <div className="shipping-data-header">
            <Typography variant="h6">Shipping Data</Typography>
            <IconButton onClick={onClose}>
              <ArrowForward />
            </IconButton>
          </div>
          <Divider flexItem />
          <div className="shipping-data-content">
            <div>
              <Grid container spacing="17px">
                <Grid size={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Ship To Attention"
                    value={shipToEdit.ShipToAttention}
                    onChange={e => setShipToEdit({ ...shipToEdit, ShipToAttention: e.target.value })}
                    slotProps={{ htmlInput: { maxLength: fieldLengths.orders.shipToAttention } }}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Ship To Company"
                    value={shipToEdit.ShipToCompany}
                    onChange={e => setShipToEdit({ ...shipToEdit, ShipToCompany: e.target.value })}
                    slotProps={{ htmlInput: { maxLength: fieldLengths.orders.shipToCompany } }}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Ship To Address 1"
                    value={shipToEdit.ShipToAddress1}
                    onChange={e => setShipToEdit({ ...shipToEdit, ShipToAddress1: e.target.value })}
                    slotProps={{ htmlInput: { maxLength: fieldLengths.orders.shipToAddress1 } }}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Ship To Address 2"
                    value={shipToEdit.ShipToAddress2}
                    onChange={e => setShipToEdit({ ...shipToEdit, ShipToAddress2: e.target.value })}
                    slotProps={{ htmlInput: { maxLength: fieldLengths.orders.shipToAddress2 } }}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Ship To City"
                    value={shipToEdit.ShipToAddressCity}
                    onChange={e => setShipToEdit({ ...shipToEdit, ShipToAddressCity: e.target.value })}
                    slotProps={{ htmlInput: { maxLength: fieldLengths.orders.shipToAddressCity } }}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Ship To State"
                    value={shipToEdit.ShipToAddressState}
                    onChange={e => setShipToEdit({ ...shipToEdit, ShipToAddressState: e.target.value })}
                    slotProps={{ htmlInput: { maxLength: fieldLengths.orders.shipToAddressState } }}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Ship To Zip"
                    value={shipToEdit.ShipToAddressZip}
                    onChange={e => setShipToEdit({ ...shipToEdit, ShipToAddressZip: e.target.value })}
                    slotProps={{ htmlInput: { maxLength: fieldLengths.orders.shipToAddressZip } }}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Ship To Country"
                    value={shipToEdit.ShipToAddressCountry}
                    onChange={e => setShipToEdit({ ...shipToEdit, ShipToAddressCountry: e.target.value })}
                    slotProps={{ htmlInput: { maxLength: fieldLengths.orders.shipToAddressCountry } }}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Receiver Contact Name"
                    value={shipToEdit.ReceiverContactName}
                    onChange={e => setShipToEdit({ ...shipToEdit, ReceiverContactName: e.target.value })}
                    slotProps={{ htmlInput: { maxLength: fieldLengths.orders.receiverContactName } }}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Receiver Contact Email"
                    value={shipToEdit.ReceiverContactEmail}
                    onChange={e => setShipToEdit({ ...shipToEdit, ReceiverContactEmail: e.target.value })}
                    slotProps={{ htmlInput: { maxLength: fieldLengths.orders.receiverContactEmail } }}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Receiver Contact Phone"
                    value={shipToEdit.ReceiverContactPhone}
                    onChange={e => setShipToEdit({ ...shipToEdit, ReceiverContactPhone: e.target.value })}
                    slotProps={{ htmlInput: { maxLength: fieldLengths.orders.receiverContactPhone } }}
                  />
                </Grid>
              </Grid>
              <div className="shipping-data-button-container">
                <Button variant="contained" color="primary" onClick={handleSave} loading={saveStatus === 'loading'}>
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Drawer>
      <Snackbar
        open={saveStatus === 'success'}
        autoHideDuration={5000}
        onClose={() => setSaveStatus('')}
        message="Shipping data saved successfully"
      />
      <Snackbar open={saveStatus === 'error'}>
        <Alert severity="error" variant="filled" onClose={() => setSaveStatus('')}>
          Failed to save shipping data. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </>
  )
}
